import React, { useRef, useEffect } from "react";
import { Star, Trash2, ArrowUp, ArrowDown, Film, Image as ImageIcon } from "lucide-react";
import FileUploader from "@/components/admin/FileUploader";

/** Manages route media (images + videos): {type,url,posterUrl,alt,title,isMain,order}. */
export default function RouteMediaManager({ media = [], onChange, testid = "media-manager" }) {
  // Ref tracks latest media so sequential uploads (multi-image) work correctly.
  const ref = useRef(media);
  useEffect(() => {
    ref.current = media;
  }, [media]);

  const commit = (next) => {
    const cleaned = next.map((m, i) => ({ ...m, order: i }));
    if (cleaned.length && !cleaned.some((i) => i.isMain)) cleaned[0].isMain = true;
    ref.current = cleaned;
    onChange(cleaned);
  };

  const addImage = (res) => {
    const prev = ref.current || [];
    commit([
      ...prev,
      {
        type: "image",
        url: res.url,
        posterUrl: "",
        alt: { en: "", es: "", ru: "" },
        title: { en: "", es: "", ru: "" },
        isMain: prev.length === 0,
        order: prev.length,
      },
    ]);
  };
  const addVideo = (res) => {
    const prev = ref.current || [];
    commit([
      ...prev,
      {
        type: "video",
        url: res.url,
        posterUrl: "",
        alt: { en: "", es: "", ru: "" },
        title: { en: "", es: "", ru: "" },
        isMain: prev.length === 0,
        order: prev.length,
      },
    ]);
  };
  const setPoster = (i, res) => {
    const arr = [...ref.current];
    arr[i] = { ...arr[i], posterUrl: res.url };
    commit(arr);
  };
  const remove = (i) => commit(ref.current.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const arr = [...ref.current];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    commit(arr);
  };
  const makeMain = (i) => commit(ref.current.map((m, idx) => ({ ...m, isMain: idx === i })));

  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-2 gap-3">
        <FileUploader accept="image/*" multiple onUploaded={addImage} testid={`${testid}-img-up`} label="Upload images" />
        <FileUploader accept="video/*" onUploaded={addVideo} testid={`${testid}-vid-up`} label="Upload video" />
      </div>

      {media.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
          {media.map((m, i) => (
            <div key={`${m.url}-${i}`} className="border border-[#2A2A2A] bg-[#0C0C0C]" data-testid={`${testid}-item-${i}`}>
              <div className="relative aspect-[4/3] bg-[#1a1a1a] flex items-center justify-center">
                {m.type === "video" ? (
                  m.posterUrl ? <img src={m.posterUrl} alt="" className="w-full h-full object-cover" />
                              : <Film className="text-[#C9A55A]" size={28}/>
                ) : (
                  <img src={m.url} alt="" className="w-full h-full object-cover" />
                )}
                <div className="absolute top-1.5 left-1.5 text-[10px] tracking-[0.16em] uppercase bg-[#0C0C0C]/80 text-[#C9A55A] border border-[#C9A55A]/40 px-2 py-0.5">
                  {m.type === "video" ? <Film size={10} className="inline mr-1"/> : <ImageIcon size={10} className="inline mr-1"/>}
                  {m.type}
                </div>
                {m.isMain && (
                  <div className="absolute top-1.5 right-1.5 text-[10px] tracking-[0.16em] uppercase bg-[#C9A55A] text-[#0C0C0C] px-2 py-0.5 font-semibold">Main</div>
                )}
              </div>
              <div className="p-2 space-y-2">
                <div className="flex items-center gap-1">
                  <button onClick={() => makeMain(i)} disabled={m.isMain} title="Main"
                    className="flex-1 p-2 border border-[#2A2A2A] text-[#F5F1EB] hover:border-[#C9A55A] hover:text-[#C9A55A] disabled:opacity-40">
                    <Star size={12} fill={m.isMain ? "#C9A55A" : "none"} className="mx-auto" />
                  </button>
                  <button onClick={() => move(i, -1)} disabled={i === 0} title="Up"
                    className="flex-1 p-2 border border-[#2A2A2A] text-[#F5F1EB] hover:border-[#C9A55A] hover:text-[#C9A55A] disabled:opacity-40">
                    <ArrowUp size={12} className="mx-auto"/>
                  </button>
                  <button onClick={() => move(i, 1)} disabled={i === media.length - 1} title="Down"
                    className="flex-1 p-2 border border-[#2A2A2A] text-[#F5F1EB] hover:border-[#C9A55A] hover:text-[#C9A55A] disabled:opacity-40">
                    <ArrowDown size={12} className="mx-auto"/>
                  </button>
                  <button onClick={() => remove(i)} title="Delete"
                    className="flex-1 p-2 border border-[#2A2A2A] text-[#F5F1EB] hover:border-[#B8342A] hover:text-[#B8342A]">
                    <Trash2 size={12} className="mx-auto"/>
                  </button>
                </div>
                {m.type === "video" && !m.posterUrl && (
                  <FileUploader accept="image/*" onUploaded={(r) => setPoster(i, r)}
                    testid={`${testid}-poster-${i}`} label="Upload poster" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
